import React, { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatBusinessDateTime } from 'config/time';
import FetchFailed from 'reusable/FetchFailed';
import Pagination from 'reusable/pagination';
import Search from 'reusable/search';
import TableScroll from 'reusable/TableScroll';
import { useLatestRequest } from 'reusable/useLatestRequest';
import {
  Account,
  deactivateManager,
  InvitedManager,
  inviteManager,
  listManagers,
  OPERATOR_PAGE_SIZE,
  reactivateManager,
  removeManager,
  resendManagerInvitation,
} from './operatorApi';
import {
  ConfirmDialog,
  ErrorContext,
  InviteDialog,
  isUnresolved,
  StatusBadge,
  SuccessNotice,
  primaryButton,
  pageAfterRemoval,
  pageToRecover,
  useOperatorErrors,
  useOutcomeMessages,
  useRoleLabel,
} from './OperatorUi';

type Action = 'resend' | 'deactivate' | 'reactivate' | 'remove';

const CONTEXT: Record<Action, ErrorContext> = {
  resend: 'resend',
  deactivate: 'deactivate',
  reactivate: 'other',
  remove: 'other',
};

/**
 * YAC-2026-009 PR5b。会社ごとの管理者（運営）。一覧（状態：招待中・有効・無効・招待の期限切れ）、招待、
 * 招待の再送、無効化、有効に戻す、管理者の役割を外す。操作の前に確認のダイアログを出す。
 */
const CompanyManagers: React.FC<{ companyId: number }> = ({ companyId }) => {
  const { t } = useTranslation('layout');
  const errorsOf = useOperatorErrors();
  const outcomeMessages = useOutcomeMessages();
  const roleLabel = useRoleLabel();
  const titleId = useId();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<Account[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [pending, setPending] = useState<{ action: Action; account: Account } | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionErrors, setActionErrors] = useState<string[]>([]);
  const beginRequest = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    try {
      const result = await listManagers(companyId, page, search);
      if (!isCurrent()) return;
      const recover = pageToRecover(page, OPERATOR_PAGE_SIZE, { count: result.count, rows: result.results.length });
      if (recover !== null) {
        setPage(recover);
        return;
      }
      setRows(result.results);
      setCount(result.count);
      setLoadError(null);
    } catch (failure) {
      if (!isCurrent()) return;
      // ページの外（404）なら、エラーにせず1ページ目を読む。
      const recover = pageToRecover(page, OPERATOR_PAGE_SIZE, { error: failure });
      if (recover !== null) {
        setPage(recover);
        return;
      }
      console.error('Failed to fetch managers', failure);
      setLoadError(failure);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [beginRequest, companyId, page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async (invitation: { email: string; name: string }) => {
    const outcome = await inviteManager(companyId, invitation);
    const failed = outcomeMessages(outcome);
    if (failed) {
      if (outcome.status === 'unknown' || outcome.status === 'expired') load();
      return { messages: failed, unresolved: isUnresolved(outcome) };
    }
    const created: InvitedManager | undefined = outcome.status === 'created' ? outcome.response?.data : undefined;
    setInviting(false);
    setNotice(
      created?.result === 'role_added'
        ? t('operator.manager.role-added', { name: created.name })
        : t('operator.manager.invited', { email: created?.email ?? invitation.email })
    );
    load();
    return null;
  };

  const run = async () => {
    if (!pending || busy) return;
    const { action, account } = pending;
    setBusy(true);
    setActionErrors([]);
    try {
      let message: string;
      if (action === 'resend') {
        await resendManagerInvitation(companyId, account.id);
        message = t('operator.manager.resent', { email: account.email });
      } else if (action === 'deactivate') {
        await deactivateManager(companyId, account.id);
        message = t('operator.manager.deactivated', { name: account.name });
      } else if (action === 'reactivate') {
        await reactivateManager(companyId, account.id);
        message = t('operator.manager.reactivated', { name: account.name });
      } else {
        const result = await removeManager(companyId, account.id);
        message = t(result === 'deleted' ? 'operator.manager.removed-deleted' : 'operator.manager.removed-role', {
          name: account.name,
        });
      }
      setPending(null);
      setNotice(message);
      // 外したら一覧から消える。今のページの最後の1件なら、1つ前のページを読む（#387 の Codex の指摘 4368575053）。
      const next = action === 'remove' ? pageAfterRemoval(page, rows.length) : page;
      if (next !== page) setPage(next);
      else load();
    } catch (failure) {
      console.error(`Failed to ${action} manager`, failure);
      setActionErrors(errorsOf(failure, CONTEXT[action]));
    } finally {
      setBusy(false);
    }
  };

  const open = (action: Action, account: Account) => {
    setActionErrors([]);
    setPending({ action, account });
  };

  const dialog = pending
    ? {
        resend: {
          title: t('operator.manager.resend-title'),
          body: t('operator.manager.resend-confirm', { name: pending.account.name }),
          label: t('operator.manager.resend'),
          danger: false,
        },
        deactivate: {
          title: t('operator.manager.deactivate-title'),
          body: t('operator.manager.deactivate-confirm', { name: pending.account.name }),
          label: t('operator.manager.deactivate'),
          danger: true,
        },
        reactivate: {
          title: t('operator.manager.reactivate-title'),
          body: t('operator.manager.reactivate-confirm', { name: pending.account.name }),
          label: t('operator.manager.reactivate'),
          danger: false,
        },
        remove: {
          title: t('operator.manager.remove-title'),
          body: t('operator.manager.remove-confirm', { name: pending.account.name }),
          label: t('operator.manager.remove'),
          danger: true,
        },
      }[pending.action]
    : null;

  const actionButton = 'rounded-md border border-neutral/100 px-2 py-1 text-caption text-neutral/300';

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4 rounded-md bg-white p-6">
      <div className="flex items-center justify-between gap-4">
        <h2 id={titleId} className="text-subtitle-1 font-bold text-neutral/300">
          {t('operator.manager.title')}
        </h2>
        <button type="button" className={primaryButton} onClick={() => setInviting(true)}>
          {t('operator.manager.invite')}
        </button>
      </div>
      <SuccessNotice message={notice} onClose={() => setNotice(null)} />
      <div className="w-80">
        <Search
          placeholder={t('operator.account.search')}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
        />
      </div>
      {loading ? (
        <div role="status" className="py-6 text-center text-neutral/300">
          {t('ui.loading', { ns: 'common' })}
        </div>
      ) : loadError ? (
        // 会社が削除された（一覧の API は会社が無ければ 404）。
        <FetchFailed error={loadError} onRetry={load} notFoundMessage={t('operator.company.not-found')} />
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-neutral/300">{t('operator.manager.empty')}</p>
      ) : (
        <TableScroll labelledBy={titleId}>
          <table className="w-full text-left text-body-2 text-neutral/300">
            <thead className="bg-primary/50">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-name')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-email')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-status')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-roles')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-expires')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((account) => {
                const invited = account.status === 'invited' || account.status === 'invitation_expired';
                return (
                  <tr key={account.id} className="border-b">
                    <td className="px-3 py-3 font-medium">{account.name || '-'}</td>
                    <td className="px-3 py-3">{account.email}</td>
                    <td className="px-3 py-3">
                      <StatusBadge status={account.status} />
                    </td>
                    <td className="px-3 py-3">{roleLabel(account.roles) || '-'}</td>
                    <td className="px-3 py-3">
                      {invited ? formatBusinessDateTime(account.invitation_expires_at, '-') : '-'}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2">
                        {invited && account.is_active ? (
                          <button
                            type="button"
                            className={actionButton}
                            aria-label={t('operator.action-label', { action: t('operator.manager.resend'), name: account.name })}
                            onClick={() => open('resend', account)}
                          >
                            {t('operator.manager.resend')}
                          </button>
                        ) : null}
                        {account.is_active ? (
                          <button
                            type="button"
                            className={actionButton}
                            aria-label={t('operator.action-label', { action: t('operator.manager.deactivate'), name: account.name })}
                            onClick={() => open('deactivate', account)}
                          >
                            {t('operator.manager.deactivate')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={actionButton}
                            aria-label={t('operator.action-label', { action: t('operator.manager.reactivate'), name: account.name })}
                            onClick={() => open('reactivate', account)}
                          >
                            {t('operator.manager.reactivate')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="rounded-md border border-red-700 px-2 py-1 text-caption text-red-700"
                          aria-label={t('operator.action-label', { action: t('operator.manager.remove'), name: account.name })}
                          onClick={() => open('remove', account)}
                        >
                          {t('operator.manager.remove')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      )}
      <Pagination allRows={count} rowsPerPage={OPERATOR_PAGE_SIZE} currentPage={page} onPageChange={setPage} />
      {inviting ? (
        <InviteDialog
          title={t('operator.manager.invite-title')}
          lead={t('operator.manager.invite-lead')}
          submitLabel={t('operator.account.send-invite')}
          onSubmit={invite}
          onClose={() => setInviting(false)}
        />
      ) : null}
      {pending && dialog ? (
        <ConfirmDialog
          title={dialog.title}
          body={<p>{dialog.body}</p>}
          confirmLabel={dialog.label}
          danger={dialog.danger}
          busy={busy}
          errors={actionErrors}
          onConfirm={run}
          onClose={() => setPending(null)}
        />
      ) : null}
    </section>
  );
};

export default CompanyManagers;
